import type { ProductSizeInterface } from '../../../../../../Interface/ProductSize';

/** Prices of one line sheet item as the trade show sells it. */
export interface LineSheetPrice {
  /** VTS price per unit, null when the item has no price. */
  current: number | null;
  /** Regular price per unit, only set when it is above the VTS price. */
  regular: number | null;
  /** VTS discount in percent, 0 without discount. */
  discount: number;
  currency: string;
}

const DEFAULT_CURRENCY = 'EUR';
const currencyFormats = new Map<string, Intl.NumberFormat>();
const numberFormat = new Intl.NumberFormat('en-GB');
const percentFormat = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

function isPrice(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** The VTS price block of an item; the shop price is the fallback without VTS info. */
export function lineSheetPrice(item: ProductSizeInterface): LineSheetPrice {
  const vts = item.vts_info;
  const current = isPrice(vts?.current_price)
    ? vts.current_price
    : isPrice(item.current_price)
    ? item.current_price
    : isPrice(item.price)
    ? item.price
    : null;
  const regularCandidate = isPrice(vts?.regular_price)
    ? vts.regular_price
    : isPrice(item.price_regular)
    ? item.price_regular
    : null;
  const regular =
    current !== null && regularCandidate !== null && regularCandidate > current
      ? regularCandidate
      : null;

  return {
    current,
    regular,
    discount: isPrice(vts?.discount) && vts.discount > 0 ? vts.discount : 0,
    currency: item.price_currency || DEFAULT_CURRENCY,
  };
}

/** Formats an amount as currency, e.g. "€16.73". */
export function formatPrice(value: number, currency: string): string {
  let format = currencyFormats.get(currency);
  if (!format) {
    try {
      format = new Intl.NumberFormat('en-GB', { style: 'currency', currency });
    } catch (e) {
      format = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: DEFAULT_CURRENCY,
      });
    }
    currencyFormats.set(currency, format);
  }

  return format.format(value);
}

/** Formats a count or measure, e.g. "1,500". */
export function formatNumber(value: number): string {
  return numberFormat.format(value);
}

/** Formats a discount badge, e.g. "−15 %" (with U+2212 minus sign). */
export function formatDiscount(percent: number): string {
  return '−' + percentFormat.format(percent) + ' %';
}
